import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { apiClient } from '../lib/apiClient'
import styles from './LoginPage.module.css'
import { Shield } from '../components/icons/Icons'

export default function VerifyEmailPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [status, setStatus] = useState<'pending' | 'verifying' | 'success' | 'error'>('pending')
  const [errorMessage, setErrorMessage] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [resendMessage, setResendMessage] = useState('')

  const senderEmail = 'noreply@allocatiq.com'
  const userEmail = user?.email || 'your email address'

  useEffect(() => {
    if (token) {
      handleVerifyToken(token)
    }
  }, [token])

  const handleVerifyToken = async (verifyToken: string) => {
    setStatus('verifying')
    try {
      await apiClient.getPublic<{ success: boolean }>(`/auth/verify-email?token=${encodeURIComponent(verifyToken)}`)
      setStatus('success')
      setTimeout(() => navigate('/login'), 3000)
    } catch (err: any) {
      setStatus('error')
      setErrorMessage(err.message || 'This verification link is invalid or has expired.')
    }
  }

  const handleResend = async () => {
    if (!user) {
      setResendMessage('Please sign in first to resend the verification email.')
      return
    }
    setResendLoading(true)
    setResendMessage('')
    try {
      await apiClient.post('/auth/resend-verification', {})
      setResendMessage('A new verification email has been sent. Please check your inbox.')
    } catch (err: any) {
      setResendMessage(err.message || 'Failed to resend. Please try again.')
    } finally {
      setResendLoading(false)
    }
  }

  if (status === 'verifying') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ width: 36, height: 36, border: '3px solid #edebe9', borderTopColor: '#0078d4', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
            <p style={{ fontSize: 15, color: '#605e5c', margin: 0 }}>Verifying your email address...</p>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#dff6dd', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#107c10" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#107c10', margin: '0 0 8px' }}>Email verified!</h2>
            <p style={{ fontSize: 14, color: '#605e5c', margin: '0 0 20px' }}>Your email address has been confirmed. Redirecting you to sign in...</p>
            <Link to="/login" style={{ color: '#0078d4', fontSize: 14, textDecoration: 'none', fontWeight: 600 }}>Sign in now</Link>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.logo}>
            <div className={styles.logoIcon}><Shield size={28} /></div>
            <div>
              <h1 className={styles.appName}>AllocatIQ</h1>
              <p className={styles.tagline}>Resource Forecasting & Utilization</p>
            </div>
          </div>
          <div style={{ marginBottom: 20, padding: '12px 16px', background: '#fde7e9', color: '#d13438', borderRadius: 4, fontSize: 14 }}>
            {errorMessage}
          </div>
          <p style={{ fontSize: 14, color: '#605e5c', marginBottom: 20, lineHeight: 1.6 }}>
            Request a new verification email below, then check your inbox at <strong>{userEmail}</strong>.
          </p>
          {resendMessage && (
            <div style={{ marginBottom: 16, padding: '10px 14px', background: resendMessage.startsWith('A new') ? '#dff6dd' : '#fde7e9', color: resendMessage.startsWith('A new') ? '#107c10' : '#d13438', borderRadius: 4, fontSize: 13 }}>
              {resendMessage}
            </div>
          )}
          <button
            onClick={handleResend}
            disabled={resendLoading}
            style={{ padding: '10px 20px', background: '#0078d4', color: '#fff', border: 'none', borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: '100%', marginBottom: 12 }}
          >
            {resendLoading ? 'Sending...' : 'Resend verification email'}
          </button>
          <Link to="/login" style={{ display: 'block', textAlign: 'center', color: '#0078d4', fontSize: 13, textDecoration: 'none' }}>Back to sign in</Link>
        </div>
      </div>
    )
  }

  // status === 'pending' — no token in URL, show the waiting screen
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <div className={styles.logoIcon}><Shield size={28} /></div>
          <div>
            <h1 className={styles.appName}>AllocatIQ</h1>
            <p className={styles.tagline}>Resource Forecasting & Utilization</p>
          </div>
        </div>

        <h2 className={styles.heading}>Check your email</h2>

        <div style={{ marginBottom: 24, padding: 20, background: '#deecf9', borderRadius: 6, border: '1px solid #b3d4f0' }}>
          <p style={{ margin: '0 0 12px', fontSize: 14, color: '#323130', lineHeight: 1.6 }}>
            We sent a verification link to <strong>{userEmail}</strong>. Click the link in that email to activate your account.
          </p>
          <p style={{ margin: 0, fontSize: 13, color: '#323130', lineHeight: 1.6 }}>
            The email is sent from <strong>{senderEmail}</strong>.
          </p>
        </div>

        <div style={{ marginBottom: 24, padding: 16, background: '#f9f9f9', borderRadius: 6, border: '1px solid #edebe9' }}>
          <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: '#323130' }}>Didn't receive the email?</p>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#605e5c', lineHeight: 1.8 }}>
            <li>Check your <strong>junk or spam</strong> folder</li>
            <li>Search for an email from <strong>{senderEmail}</strong></li>
            <li>If your organization filters emails, ask your IT administrator to <strong>allowlist</strong> the domain <em>allocatiq.com</em></li>
            <li>The link expires in <strong>24 hours</strong></li>
          </ul>
        </div>

        {resendMessage && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: resendMessage.startsWith('A new') ? '#dff6dd' : '#fde7e9', color: resendMessage.startsWith('A new') ? '#107c10' : '#d13438', borderRadius: 4, fontSize: 13 }}>
            {resendMessage}
          </div>
        )}

        <button
          onClick={handleResend}
          disabled={resendLoading}
          style={{ padding: '10px 20px', background: '#fff', color: '#0078d4', border: '1px solid #0078d4', borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: '100%', marginBottom: 12 }}
        >
          {resendLoading ? 'Sending...' : 'Resend verification email'}
        </button>

        <p style={{ textAlign: 'center', margin: 0 }}>
          <Link to="/login" style={{ color: '#0078d4', fontSize: 13, textDecoration: 'none' }}>Back to sign in</Link>
        </p>
      </div>
    </div>
  )
}
